export interface ImportErrorDetail {
    column?: string;
    value?: unknown;
    message?: string;
    expectedType?: string;
    attemptedFormats?: unknown;
}

export interface ImportErrorRow {
    row: number;
    errors: ImportErrorDetail[];
}

export interface FlatImportError {
    row: number;
    column: string;
    value?: unknown;
    message: string;
    expectedType?: string;
    attemptedFormats?: unknown;
}

function normalizeErrorMessage(message: unknown): string {
    return String(message ?? '')
        .replace(/'[^']*'/g, "'*'")
        .trim();
}

export function buildFilteredErrors(importErrors: unknown, perGroupLimit: number = 10): FlatImportError[] {
    const inputRows = Array.isArray(importErrors) ? (importErrors as ImportErrorRow[]) : [];

    const allErrors: FlatImportError[] = [];

    for (const rowEntry of inputRows) {
        const rowNumber = Number((rowEntry as any)?.row);
        const errors = Array.isArray((rowEntry as any)?.errors) ? (rowEntry as any).errors : [];

        for (const err of errors) {
            allErrors.push({
                row: Number.isFinite(rowNumber) ? rowNumber : 0,
                column: String((err as any)?.column ?? '_unknown_'),
                value: (err as any)?.value,
                message: String((err as any)?.message ?? ''),
                expectedType: (err as any)?.expectedType,
                attemptedFormats: (err as any)?.attemptedFormats
            });
        }
    }

    const groups: Record<string, FlatImportError[]> = {};

    for (const err of allErrors) {
        const normalizedMessage = normalizeErrorMessage(err.message);
        const key = `${err.column || '_unknown_'}|${normalizedMessage || ''}`;

        if (!groups[key]) {
            groups[key] = [];
        }

        groups[key].push({
            ...err,
            message: err.message ?? ''
        });
    }

    const limitedErrors: FlatImportError[] = [];

    for (const key of Object.keys(groups)) {
        const group = groups[key];
        group.sort((a, b) => a.row - b.row);
        limitedErrors.push(...group.slice(0, Math.max(0, perGroupLimit)));
    }

    limitedErrors.sort((a, b) => a.row - b.row);
    return limitedErrors;
}

export function formatErrorValue(value: unknown, maxLength: number = 120): string {
    if (value === null || value === undefined) {
        return '';
    }

    let text: string;

    if (value instanceof Date) {
        text = value.toISOString();
    }
    else if (typeof value === 'object') {
        try {
            text = JSON.stringify(value);
        }
        catch {
            text = String(value);
        }
    }
    else {
        text = String(value);
    }

    if (text.length > maxLength) {
        const sliceLength = Math.max(0, maxLength - 3);
        return text.slice(0, sliceLength) + '...';
    }

    return text;
}
